import React, { useCallback, useState } from 'react';

import { FormControl, useConfirm } from '@primer/react';

import Button from '../../common/Button/Button';
import CardView from '../../common/CardView/CardView';
import ModalOverlay from '../../common/ModalOverlay/ModalOverlay';

import PipelineCreationFlow from '../PipelineCreationFlow/PipelineCreationFlow';

import { ModelSearchProvider } from '../../models/ModelSearchContext';
import { PipelineCreationProvider } from '../PipelineCreationContext';

import usePipelineData from '../PipelineDataContext';

const PipelineDeploymentPanel = () => {
  const [isCreateMenuOpen, setCreateMenuOpen] = useState(false);
  const [deploymentError, setDeploymentError] = useState(false);

  const {
    latestPipelineDeployment, pipeline, deploy, undeploy, refetchPipelineHistory,
  } = usePipelineData();

  const confirm = useConfirm();

  const pipelineID = pipeline?.id;
  const pipelineName = pipeline?.pipelineID;

  const deployed = pipeline?.deployment?.deployed ?? false;
  const handleDeployment = useCallback(async (deploymentPrimaryKey) => {
    const confirmOptions = {
      title: 'Deploy Pipeline',
      content: 'Are you sure you want to deploy this pipeline?',
      confirmButtonContent: 'Deploy',
      confirmButtonType: 'primary'
    };

    setDeploymentError(false);
    if (await confirm(confirmOptions)) {
      deploy(deploymentPrimaryKey).then(() => {
        setDeploymentError(false)
      }).catch((() => {
        setDeploymentError(true);
      }));
    }
  }, [confirm, deploy]);

  const handleUndeployment = useCallback(async (deploymentPrimaryKey) => {
    const confirmOptions = {
      title: 'Undeploy Pipeline',
      content: 'Are you sure you want to undeploy this pipeline?',
      confirmButtonContent: 'Undeploy',
      confirmButtonType: 'danger'
    };

    setDeploymentError(false);
    if (await confirm(confirmOptions)) {
      undeploy(deploymentPrimaryKey).then(() => {
        setDeploymentError(false);
      }).catch((() => {
        setDeploymentError(true);
      }));
    }
  }, [confirm, undeploy]);

  const onDeployClick = useCallback(() => {
    if (deployed) {
      handleUndeployment({ pipeline_id: pipelineID });
    } else {
      handleDeployment(pipelineName)
    }
  }, [deployed, handleUndeployment, pipelineID, handleDeployment, pipelineName])

  const errorMessage = `Something went wrong while trying to ${deployed ? 'undeploy' : 'deploy'} your pipeline.`;
  return (
    <>
      <CardView className="mb-2">
        <FormControl>
          <FormControl.Label visuallyHidden>{deployed ? 'Undeploy' : 'Deploy'}</FormControl.Label>
          <Button
            className="w-full"
            size="large"
            variant={deployed ? 'danger' : 'primary'}
            onClick={onDeployClick}
          >
            {deployed ? 'Undeploy' : 'Deploy'}
          </Button>
          {
            deploymentError && (
              <FormControl.Validation variant="error">
                {errorMessage}
              </FormControl.Validation>
            )
          }
        </FormControl>
      </CardView>
      {
        isCreateMenuOpen ? (
          <ModalOverlay
            animationKey="create-pipeline-overlay"
            active={isCreateMenuOpen}
            onModalToggle={setCreateMenuOpen}
          >
            <PipelineCreationProvider pipelineRefetch={refetchPipelineHistory}>
              <ModelSearchProvider>
                <PipelineCreationFlow onClose={() => setCreateMenuOpen(false)} />
              </ModelSearchProvider>
            </PipelineCreationProvider>
          </ModalOverlay>
        ) : null
      }
    </>
  )
}

export default PipelineDeploymentPanel;
